import { useEffect, useMemo, useState } from "react";
import {
  Package, PackageCheck, PackageX, Truck, MapPin, ChevronRight, Loader2,
  ArrowLeft, CheckCircle2, XCircle, Clock, Zap, Wallet, Trophy, Sparkles,
  AlertTriangle, Mail, RotateCcw, Repeat, Phone, Search, X,
  CheckSquare, Square, ListChecks, User2, Map as MapIcon, ChevronDown, ChevronUp,
  Download, FileText,
} from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { toast } from "sonner";
import { Routes as RoutesAPI } from "../lib/api";
import { API } from "../lib/api";
import { getCurrentLocation } from "../lib/geo";
import { useConfirm } from "../components/ConfirmProvider";
import SettleSummaryModal from "../components/SettleSummaryModal";
import { playSuccess, playError, playClick, playSettle } from "../lib/sfx";
import { emitXp } from "../components/XpBurst";
import { PageSkeleton } from "../components/PageSkeleton";

// Extract phone digits from raw string (handles "+48 601 234 567", "601-234-567" etc.)
const stripPhone = (p) => (p || "").toString().replace(/[^\d+]/g, "");

// Build a full postal-style address
const fullAddress = (s) => {
  const parts = [];
  if (s.address_text) parts.push(s.address_text);
  const zipCity = [s.zip, s.city].filter(Boolean).join(" ").trim();
  if (zipCity) parts.push(zipCity);
  return parts.join(", ");
};

// Multipack grouping key — MUST match backend `_normalize_stop_addr`.
const stopGroupKey = (s) => {
  const parts = [
    (s.address_text || "").trim().toLowerCase(),
    (s.zip || "").trim().toLowerCase(),
    (s.city || "").trim().toLowerCase(),
  ];
  const k = parts.join("|").replace(/\s+/g, " ");
  if (!k.replace(/\|/g, "").trim()) return `__lone__${s.final_order}`;
  return k;
};

// Re-order stops so multipack siblings sit next to each other.
const groupStopsByAddress = (stops) => {
  const map = new Map();
  stops.forEach((s) => {
    const key = stopGroupKey(s);
    const g = map.get(key);
    if (g) {
      g.stops.push(s);
      g.first = Math.min(g.first, s.final_order || 999999);
    } else {
      map.set(key, { stops: [s], first: s.final_order || 999999 });
    }
  });
  return Array.from(map.values())
    .sort((a, b) => a.first - b.first)
    .flatMap((g) => g.stops.sort(
      (a, b) => (a.final_order || 0) - (b.final_order || 0),
    ));
};

const UNDELIVERED_REASONS = [
  { id: "awizo",      label: "Awizo",              icon: Mail,     tone: "from-amber-500 to-orange-700" },
  { id: "return",     label: "Zwrot do nadawcy",   icon: RotateCcw, tone: "from-red-500 to-rose-800" },
  { id: "redelivery", label: "Powtórne doręczenie",icon: Repeat,   tone: "from-purple-500 to-fuchsia-800" },
];

/**
 * "Praca" — the driver's work console. Lists every open assignment,
 * lets them click through to a per-package status editor, and settle
 * the whole thing once every package has a status.
 */
export default function WorkPage({ embedded = false }) {
  const [selected, setSelected] = useState(null); // assignment_id
  return selected ? (
    <PackagesView assignmentId={selected} onBack={() => setSelected(null)} />
  ) : (
    <AssignmentsList onOpen={setSelected} embedded={embedded} />
  );
}

function AssignmentsList({ onOpen, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    RoutesAPI.listMyWork()
      .then((d) => setRows(d.assignments || []))
      .catch(() => toast.error("Nie mogę pobrać zleceń"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCount = rows.filter((r) => !r.settled_at).length;
  const settledCount = rows.filter((r) => r.settled_at).length;

  if (loading) {
    return <PageSkeleton label="Ładuję pracę…" />;
  }

  return (
    <div className={`${embedded ? "" : "p-3 md:p-6"} space-y-5`} data-testid="work-page">
      {!embedded && (
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-500 via-red-700 to-zinc-950 text-white p-6 md:p-8 ring-1 ring-white/10 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.7)]">
        <Truck className="absolute -right-10 -bottom-16 w-72 h-72 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <Truck className="w-5 h-5" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // Praca · Twoje zlecenia
          </div>
        </div>
        <h1 className="relative mt-3 font-heading font-black text-3xl md:text-5xl uppercase tracking-tight">
          Praca
        </h1>
        <div className="relative mt-1 text-white/80 text-sm max-w-2xl">
          Kliknij zlecenie, nadaj status każdej paczce (doręczone / awizo / nieudane),
          potem kliknij <b>„Rozlicz zlecenie&rdquo;</b> — dostaniesz XP, pieniądze do portfela
          i licznik doręczonych paczek się aktualizuje.
        </div>
        <div className="relative mt-5 flex gap-3 flex-wrap">
          <Chip label="Otwarte" value={openCount} tone="cyan" />
          <Chip label="Rozliczone" value={settledCount} tone="emerald" />
        </div>
      </div>
      )}
      {embedded && (
        <div className="flex items-center gap-3 flex-wrap">
          <Chip label="Otwarte" value={openCount} tone="cyan" />
          <Chip label="Rozliczone" value={settledCount} tone="emerald" />
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {rows.length} zleceń łącznie
          </span>
        </div>
      )}

      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="p-12 text-center text-sm text-zinc-500 bg-white rounded-2xl ring-1 ring-zinc-200">
            Nie masz jeszcze przypisanych zleceń. Poczekaj aż admin przypisze Ci trasę.
          </div>
        )}
        {rows.map((a) => (
          <AssignmentCard key={a.id} asg={a} onOpen={() => onOpen(a.id)} />
        ))}
      </div>
    </div>
  );
}

function AssignmentCard({ asg, onOpen }) {
  const pct = asg.total_packages > 0
    ? Math.round(((asg.delivered + asg.awizo) / asg.total_packages) * 100)
    : 0;
  const settled = !!asg.settled_at;

  return (
    <button
      onClick={onOpen}
      data-testid={`work-card-${asg.id}`}
      className={`w-full text-left rounded-2xl p-4 ring-1 transition-all hover:shadow-lg ${
        settled
          ? "bg-emerald-50 ring-emerald-200"
          : asg.can_settle
            ? "bg-white ring-orange-300 shadow-orange-100 shadow-md"
            : "bg-white ring-zinc-200 hover:ring-zinc-400"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ring-1 ${
          settled ? "bg-emerald-500 text-white ring-emerald-400"
                  : "bg-gradient-to-br from-orange-500 to-red-600 text-white ring-white/30"
        }`}>
          <Package className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-black uppercase tracking-tight text-lg text-zinc-900 truncate">
              {asg.plan_name || "Bez nazwy"}
            </div>
            {settled && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-mono uppercase tracking-widest">
                <CheckCircle2 className="w-3 h-3" /> Rozliczone
              </span>
            )}
            {!settled && asg.can_settle && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-mono uppercase tracking-widest animate-pulse">
                <Sparkles className="w-3 h-3" /> Gotowe do rozliczenia
              </span>
            )}
          </div>
          <div className="text-[11px] font-mono text-zinc-500 mt-1 flex flex-wrap items-center gap-2">
            {asg.vehicle_plate && (
              <span className="px-1.5 py-0.5 rounded bg-zinc-900 text-white text-[10px]">
                {asg.vehicle_plate}
              </span>
            )}
            {asg.assigned_at && (
              <span>{new Date(asg.assigned_at).toLocaleDateString("pl-PL")}</span>
            )}
            <span>· {asg.total_packages} paczek</span>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  settled ? "bg-emerald-500"
                          : asg.can_settle ? "bg-gradient-to-r from-orange-500 to-red-600"
                          : "bg-gradient-to-r from-cyan-500 to-blue-600"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex items-center gap-3 mt-2 text-[11px] font-mono">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <PackageCheck className="w-3 h-3" /> {asg.delivered}
              </span>
              <span className="inline-flex items-center gap-1 text-amber-700">
                <PackageX className="w-3 h-3" /> {asg.awizo}
              </span>
              <span className="inline-flex items-center gap-1 text-zinc-500">
                <Clock className="w-3 h-3" /> {asg.pending}
              </span>
              <span className="ml-auto font-bold text-zinc-700">{pct}%</span>
            </div>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-400 mt-3" />
      </div>
    </button>
  );
}

function PackagesView({ assignmentId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [settling, setSettling] = useState(false);
  const [reasonFor, setReasonFor] = useState(null);
  const [query, setQuery] = useState("");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkReason, setBulkReason] = useState(false); // reason picker for bulk niedoręczona
  const [bulkBusy, setBulkBusy] = useState(false);
  const [settleSummary, setSettleSummary] = useState(null); // post-settle recap for the driver
  const [mapOpen, setMapOpen] = useState(false); // Admin GPS audit map toggle
  const { confirm } = useConfirm();

  const load = () => {
    setLoading(true);
    RoutesAPI.listAssignmentPackages(assignmentId)
      .then(setData)
      .catch(() => toast.error("Nie mogę pobrać paczek"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [assignmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (stop, status, reason = null) => {
    setSavingId(stop.final_order);
    if (status === "done") {
      playSuccess();
      // Emit +1 PD only on transition INTO "done" — toggling stays silent.
      if (stop.status !== "done") emitXp(1);
    }
    else if (status === "skipped") playError();
    else playClick();
    try {
      const location = status === "pending" ? null : await getCurrentLocation(3500);
      await RoutesAPI.updateStopStatus(data.plan_id, stop.final_order, status, reason, location);
      setData({
        ...data,
        stops: data.stops.map((s) =>
          s.final_order === stop.final_order
            ? { ...s, status, undelivered_reason: reason || undefined }
            : s,
        ),
      });
      if (reason) {
        const label = UNDELIVERED_REASONS.find((r) => r.id === reason)?.label;
        toast.success(`Niedoręczona: ${label}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSavingId(null);
    }
  };

  const askUndelivered = (stop) => setReasonFor(stop);
  const commitUndelivered = (reason) => {
    if (reasonFor) updateStatus(reasonFor, "skipped", reason);
    setReasonFor(null);
  };

  // ---------- Bulk selection helpers ----------
  const toggleSelect = (finalOrder) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(finalOrder)) next.delete(finalOrder);
      else next.add(finalOrder);
      return next;
    });
  };
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelected(new Set());
    setBulkReason(false);
  };
  // Bulk apply a status to all currently-selected stops (in parallel).
  const bulkApply = async (status, reason = null) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    if (status === "done") playSuccess();
    else if (status === "skipped") playError();
    else playClick();
    const targets = data.stops.filter((s) => selected.has(s.final_order));
    // Count how many of the targets were NOT already "done" so the XP burst
    // reflects only real transitions (not idempotent re-marks).
    const newlyDelivered = status === "done"
      ? targets.filter((s) => s.status !== "done").length
      : 0;
    // Fetch geolocation once for the whole batch (no need to ask 10 times).
    const location = status === "pending" ? null : await getCurrentLocation(3500);
    let ok = 0;
    let fail = 0;
    // Parallel API calls, but chunk to avoid overwhelming the server.
    const CHUNK = 10;
    for (let i = 0; i < targets.length; i += CHUNK) {
      const batch = targets.slice(i, i + CHUNK);
      const results = await Promise.allSettled(
        batch.map((s) =>
          RoutesAPI.updateStopStatus(data.plan_id, s.final_order, status, reason, location),
        ),
      );
      results.forEach((r) => (r.status === "fulfilled" ? ok++ : fail++));
    }
    // Optimistically merge into UI
    setData((prev) => ({
      ...prev,
      stops: prev.stops.map((s) =>
        selected.has(s.final_order)
          ? { ...s, status, undelivered_reason: reason || undefined }
          : s,
      ),
    }));
    setBulkBusy(false);
    exitSelectionMode();
    // Emit staggered "+1 PD" bursts for each newly-delivered package so the
    // driver visually feels the XP roll in on bulk actions. Capped so a
    // 200-parcel bulk mark doesn't overwhelm the screen.
    if (newlyDelivered > 0) {
      const bursts = Math.min(newlyDelivered, 10);
      for (let i = 0; i < bursts; i++) {
        window.setTimeout(() => emitXp(1), i * 120);
      }
    }
    if (fail === 0) {
      toast.success(`${ok} paczek zaktualizowanych`);
    } else {
      toast.warning(`Zapisano ${ok}, błąd na ${fail} paczkach`);
    }
  };

  const settle = async () => {
    if (!(await confirm(
      "Rozliczyć zlecenie? Doręczone paczki zasilają portfel, XP i licznik.",
      { confirmLabel: "Rozlicz", tone: "info" },
    ))) return;
    setSettling(true);
    try {
      const r = await RoutesAPI.settleAssignment(assignmentId);
      playSettle();
      const extra = Number(r.multipack_extra || 0);
      const full = Math.max(Number(r.delivered || 0) - extra, 0);
      const detail = extra > 0
        ? `(${full}× ${r.rate} zł + ${extra}× multipack 0,50 zł)`
        : `(${r.delivered} paczek × ${r.rate} zł)`;
      toast.success(
        `Zlecenie rozliczone! +${r.wallet_gain?.toLocaleString("pl-PL")} zł ${detail}`,
        { duration: 4000 },
      );
      // Detailed post-settle recap so the driver can verify counts + COD.
      setSettleSummary(r);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd rozliczenia");
    } finally {
      setSettling(false);
    }
  };

  const totals = useMemo(() => {
    if (!data?.stops) return { total: 0, done: 0, skipped: 0, pending: 0 };
    const total = data.stops.length;
    const done = data.stops.filter((s) => s.status === "done").length;
    const skipped = data.stops.filter((s) => s.status === "skipped").length;
    return { total, done, skipped, pending: total - done - skipped };
  }, [data]);

  const canSettle = totals.pending === 0 && totals.total > 0 && !data?.settled_at;
  const settled = !!data?.settled_at;

  if (loading || !data) {
    return <PageSkeleton label="Ładuję paczki…" />;
  }

  return (
    <div className="p-3 md:p-6 space-y-4" data-testid="work-packages-view">
      <button
        onClick={onBack}
        data-testid="btn-work-back"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
      >
        <ArrowLeft className="w-4 h-4" /> Wróć do zleceń
      </button>

      {/* Hero */}
      <div className={`relative overflow-hidden rounded-3xl p-5 md:p-6 ring-1 shadow-lg text-white transition-colors ${
        settled ? "bg-gradient-to-br from-emerald-600 to-teal-900 ring-emerald-300/30"
                : canSettle ? "bg-gradient-to-br from-orange-500 via-red-700 to-zinc-950 ring-orange-300/30"
                : "bg-gradient-to-br from-zinc-900 via-zinc-950 to-black ring-white/10"
      }`}>
        <Package className="absolute -right-8 -bottom-10 w-56 h-56 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // {settled ? "Zlecenie rozliczone" : "Zlecenie w trakcie"}
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight mt-1">
            {data.plan_name || "Zlecenie"}
          </h1>

          <div className="mt-4 flex flex-wrap gap-2">
            <MiniStat label="Wszystkie" value={totals.total} icon={Package} tone="white" />
            <MiniStat label="Doręczone" value={totals.done} icon={PackageCheck} tone="emerald" />
            <MiniStat label="Niedoręczone" value={totals.skipped} icon={PackageX} tone="amber" />
            <MiniStat label="Do zrobienia" value={totals.pending} icon={Clock} tone="cyan" />
          </div>

          <div className="mt-4">
            <div className="h-2.5 bg-black/40 rounded-full overflow-hidden ring-1 ring-white/10">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-white transition-all duration-500"
                style={{ width: `${totals.total ? Math.round(((totals.done + totals.skipped) / totals.total) * 100) : 0}%` }}
              />
            </div>
            <div className="text-[10px] font-mono text-white/70 mt-1">
              {totals.done + totals.skipped} / {totals.total} obsłużonych
            </div>
          </div>

          {!settled && (
            <button
              onClick={settle}
              disabled={!canSettle || settling}
              data-testid="btn-settle-assignment"
              className={`mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold uppercase tracking-widest transition-all active:scale-95 ${
                canSettle
                  ? "bg-white text-orange-800 hover:bg-orange-100 shadow-lg hover:scale-[1.02]"
                  : "bg-white/20 text-white/60 cursor-not-allowed"
              }`}
              title={canSettle ? "Rozlicz zlecenie" : "Nadaj status wszystkim paczkom"}
            >
              {settling ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Rozliczam…</>
              ) : canSettle ? (
                <><Trophy className="w-4 h-4" /> Rozlicz zlecenie</>
              ) : (
                <><AlertTriangle className="w-4 h-4" /> Brakuje statusu w {totals.pending}</>
              )}
            </button>
          )}
          {settled && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/15 ring-1 ring-white/20 text-sm">
              <CheckCircle2 className="w-4 h-4" /> Rozliczone {new Date(data.settled_at).toLocaleString("pl-PL")}
            </div>
          )}

          {/* Downloads — identyczne opcje jak w menu Trasy (Spoke PDF).
              Umożliwia szybkie pobranie manifestu bezpośrednio ze zlecenia. */}
          {data.plan_id && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="assignment-downloads">
              <a
                href={`${API}/routes/plans/${data.plan_id}/export-spoke.pdf`}
                download
                data-testid="btn-work-export-spoke"
                title="Manifest PDF dla Spoke (importuj jako 'Import Manifest from PDF')"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-white/15 hover:bg-white/25 backdrop-blur-md ring-1 ring-white/25 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" /> Manifest PDF (Spoke)
              </a>
              <a
                href={`${API}/routes/plans/${data.plan_id}/export-spoke.pdf?simple=1`}
                download
                data-testid="btn-work-export-spoke-simple"
                title="Uproszczony manifest PDF (bez tabeli) — użyj gdy Spoke nie parsuje wersji podstawowej"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-white/10 hover:bg-white/20 backdrop-blur-md ring-1 ring-white/15 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Manifest uproszczony
              </a>
              <a
                href={`${API}/routes/plans/${data.plan_id}/export.xlsx`}
                download
                data-testid="btn-work-export-xlsx"
                title="Pobierz Excel z paczkami trasy"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest bg-white/10 hover:bg-white/20 backdrop-blur-md ring-1 ring-white/15 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Excel
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Packages list */}
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden">
        {(() => {
          // Detect multi-driver by counting unique status_by across stops.
          const drivers = new Map();
          (data.stops || []).forEach((s) => {
            if (s.status_by && s.status_by_name) {
              if (!drivers.has(s.status_by)) {
                drivers.set(s.status_by, { name: s.status_by_name, count: 0 });
              }
              drivers.get(s.status_by).count += 1;
            }
          });
          if (drivers.size <= 1) return null;
          return (
            <div className="px-4 py-2 bg-violet-50 border-b border-violet-200 flex items-center gap-2 flex-wrap"
                 data-testid="pkg-multi-driver-summary">
              <span className="text-[10px] font-mono uppercase tracking-widest text-violet-800">
                // Trasa współdzielona — {drivers.size} kierowców
              </span>
              {Array.from(drivers.entries()).map(([uid, info]) => (
                <span
                  key={uid}
                  data-testid={`multi-driver-chip-${uid}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ring-1 ${driverTone(uid)}`}
                >
                  <User2 className="w-3 h-3" />
                  {info.name}
                  <span className="opacity-70 font-mono">· {info.count} paczek</span>
                </span>
              ))}
            </div>
          );
        })()}
        <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center gap-2">
          <Package className="w-4 h-4 text-zinc-500" />
          <div className="text-sm font-bold text-zinc-800">Paczki na trasie</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
            {data.stops.length}
          </div>
          {(() => {
            const gpsCount = (data.stops || []).filter(
              (s) => s.status_location && typeof s.status_location.lat === "number",
            ).length;
            return (
              <button
                onClick={() => setMapOpen((v) => !v)}
                data-testid="btn-toggle-audit-map"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors ${
                  mapOpen
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100"
                }`}
                title={
                  gpsCount > 0
                    ? `Mapa audytu GPS · ${gpsCount} pinów`
                    : "Brak zapisanych lokalizacji GPS"
                }
              >
                <MapIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mapa GPS</span>
                <span className="font-mono opacity-70">· {gpsCount}</span>
                {mapOpen ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
              </button>
            );
          })()}
          {!settled && (
            <button
              onClick={() => (selectionMode ? exitSelectionMode() : setSelectionMode(true))}
              data-testid="btn-toggle-selection"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors ${
                selectionMode
                  ? "bg-zinc-900 text-white hover:bg-zinc-700"
                  : "bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100"
              }`}
              title={selectionMode ? "Zakończ wybieranie" : "Wybierz wiele paczek naraz"}
            >
              <ListChecks className="w-3.5 h-3.5" />
              {selectionMode ? "Anuluj" : "Wybierz"}
            </button>
          )}
        </div>

        {/* Collapsible Admin GPS audit map — shows where each stop was
            marked as done/skipped, colored per driver for shared routes. */}
        {mapOpen && (
          <div
            data-testid="audit-map-wrapper"
            className="border-b border-zinc-100 bg-white animate-in fade-in slide-in-from-top-2 duration-200"
          >
            <AuditMap stops={data.stops} />
          </div>
        )}


        {/* Search filter */}
        <div className="px-3 py-2 border-b border-zinc-100 bg-white relative">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Szukaj po nazwisku, adresie, telefonie, nr paczki…"
            data-testid="work-search"
            className="w-full pl-10 pr-10 py-2 rounded-xl bg-zinc-50 ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm placeholder:text-zinc-400"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="work-search-clear"
              className="absolute right-6 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-200 text-zinc-500"
              title="Wyczyść"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="divide-y divide-zinc-100">
          {(() => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? data.stops.filter((s) => {
                  const hay = [s.client_name, s.address_text, s.city, s.zip, s.phone, s.parcel_number, String(s.final_order || "")]
                    .filter(Boolean).join(" ").toLowerCase();
                  return hay.includes(q);
                })
              : data.stops;
            const list = groupStopsByAddress(filtered);
            if (list.length === 0 && q) {
              return <div className="p-8 text-center text-sm text-zinc-500">Brak wyników dla &bdquo;{query}&rdquo;.</div>;
            }
            // "Zaznacz wszystkie" strip — reflects the currently VISIBLE list
            const visibleIds = list.map((s) => s.final_order);
            const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
            const someVisibleSelected = visibleIds.some((id) => selected.has(id));
            const strip = selectionMode ? (
              <div key="__select_all__" className="px-4 py-2.5 flex items-center gap-3 bg-zinc-900 text-white sticky top-0 z-10">
                <button
                  onClick={() => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
                      else visibleIds.forEach((id) => next.add(id));
                      return next;
                    });
                  }}
                  data-testid="btn-select-all"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-cyan-300"
                >
                  {allVisibleSelected ? (
                    <CheckSquare className="w-4 h-4 text-cyan-300" />
                  ) : someVisibleSelected ? (
                    <CheckSquare className="w-4 h-4 text-cyan-300/50" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {allVisibleSelected ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
                </button>
                <span className="text-[11px] font-mono text-white/70 ml-auto">
                  {selected.size} / {data.stops.length} zaznaczonych
                </span>
              </div>
            ) : null;
            return (
              <>
                {strip}
                {list.map((s) => (
                  <PackageRow
                    key={s.final_order}
                    stop={s}
                    disabled={settled || savingId === s.final_order}
                    saving={savingId === s.final_order}
                    onSetDone={() => updateStatus(s, "done")}
                    onSetPending={() => updateStatus(s, "pending")}
                    onAskUndelivered={() => askUndelivered(s)}
                    selectionMode={selectionMode}
                    isChecked={selected.has(s.final_order)}
                    onToggleSelect={() => toggleSelect(s.final_order)}
                  />
                ))}
              </>
            );
          })()}
        </div>
      </div>

      {/* Floating bulk action bar */}
      {selectionMode && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-40 flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-2xl bg-zinc-900 text-white shadow-2xl ring-1 ring-white/10 max-w-[95vw]"
        >
          <span className="text-xs font-mono uppercase tracking-widest text-white/70 pr-1 border-r border-white/10 mr-1">
            {selected.size} zaznaczone
          </span>
          <button
            disabled={selected.size === 0 || bulkBusy}
            onClick={() => bulkApply("done")}
            data-testid="btn-bulk-done"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed active:scale-90 transition-transform"
          >
            <PackageCheck className="w-3.5 h-3.5" /> Doręczono
          </button>
          <button
            disabled={selected.size === 0 || bulkBusy}
            onClick={() => setBulkReason(true)}
            data-testid="btn-bulk-undelivered"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed active:scale-90 transition-transform"
          >
            <PackageX className="w-3.5 h-3.5" /> Niedoręczona
          </button>
          <button
            disabled={selected.size === 0 || bulkBusy}
            onClick={() => bulkApply("pending")}
            data-testid="btn-bulk-pending"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-zinc-600 hover:bg-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed active:scale-90 transition-transform"
          >
            <Clock className="w-3.5 h-3.5" /> Cofnij
          </button>
          <button
            onClick={exitSelectionMode}
            data-testid="btn-bulk-cancel"
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/70 ml-1"
            title="Zamknij wybieranie"
          >
            <X className="w-4 h-4" />
          </button>
          {bulkBusy && <Loader2 className="w-4 h-4 animate-spin text-white/80" />}
        </div>
      )}

      {/* Bulk reason picker — pick a single reason applied to all selected */}
      {bulkReason && (
        <UndeliveredModal
          stop={{ client_name: `${selected.size} paczek`, address_text: "" }}
          onCancel={() => setBulkReason(false)}
          onPick={(reason) => {
            setBulkReason(false);
            bulkApply("skipped", reason);
          }}
        />
      )}

      {reasonFor && (
        <UndeliveredModal
          stop={reasonFor}
          onCancel={() => setReasonFor(null)}
          onPick={commitUndelivered}
        />
      )}

      <SettleSummaryModal
        open={!!settleSummary}
        result={settleSummary}
        planName={data?.plan_name}
        onClose={() => setSettleSummary(null)}
      />
    </div>
  );
}

// Consistent color assignment per driver — makes audit stamps visually
// distinguishable in multi-driver assignments (green/blue/purple/pink...).
const DRIVER_TONES = [
  "bg-emerald-100 text-emerald-800 ring-emerald-200",
  "bg-blue-100 text-blue-800 ring-blue-200",
  "bg-violet-100 text-violet-800 ring-violet-200",
  "bg-pink-100 text-pink-800 ring-pink-200",
  "bg-cyan-100 text-cyan-800 ring-cyan-200",
  "bg-amber-100 text-amber-800 ring-amber-200",
];
// Hex colors paired to DRIVER_TONES (same order). Used for Leaflet markers
// where Tailwind classes don't work — we need concrete CSS colors instead.
const DRIVER_HEX = [
  "#10b981", // emerald-500
  "#3b82f6", // blue-500
  "#8b5cf6", // violet-500
  "#ec4899", // pink-500
  "#06b6d4", // cyan-500
  "#f59e0b", // amber-500
];
const _driverToneCache = new Map();
function driverToneIndex(userId) {
  if (!userId) return -1;
  let h = 0;
  for (const c of userId) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
  return Math.abs(h) % DRIVER_TONES.length;
}
function driverTone(userId) {
  if (!userId) return "bg-zinc-100 text-zinc-600 ring-zinc-200";
  if (_driverToneCache.has(userId)) return _driverToneCache.get(userId);
  const tone = DRIVER_TONES[driverToneIndex(userId)];
  _driverToneCache.set(userId, tone);
  return tone;
}
function driverHex(userId) {
  if (!userId) return "#71717a"; // zinc-500
  const idx = driverToneIndex(userId);
  return DRIVER_HEX[idx];
}

function PackageRow({
  stop, disabled, saving, onSetDone, onSetPending, onAskUndelivered,
  selectionMode = false, isChecked = false, onToggleSelect,
}) {
  const status = stop.status || "pending";
  const reason = stop.undelivered_reason;
  const rowClickable = selectionMode && !disabled;
  return (
    <div
      className={`px-4 py-3 flex items-start gap-3 ${
        rowClickable ? "cursor-pointer" : ""
      } ${
        selectionMode && isChecked ? "bg-cyan-50 hover:bg-cyan-100" : "hover:bg-zinc-50"
      }`}
      data-testid={`pkg-row-${stop.final_order}`}
      onClick={rowClickable ? onToggleSelect : undefined}
    >
      {selectionMode && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
          data-testid={`pkg-check-${stop.final_order}`}
          className="mt-1 shrink-0"
          disabled={disabled}
          title={isChecked ? "Odznacz" : "Zaznacz"}
        >
          {isChecked ? (
            <CheckSquare className="w-5 h-5 text-cyan-600" />
          ) : (
            <Square className="w-5 h-5 text-zinc-400" />
          )}
        </button>
      )}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-heading font-black shrink-0 ${
        status === "done" ? "bg-emerald-100 text-emerald-700"
        : status === "skipped" ? "bg-amber-100 text-amber-700"
        : "bg-zinc-100 text-zinc-500"
      }`}>
        {stop.final_order}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-zinc-900 truncate" data-testid={`pkg-name-${stop.final_order}`}>
          {stop.client_name || `Paczka #${stop.final_order}`}
        </div>
        <div className="text-[11px] font-mono text-zinc-500 mt-0.5 flex items-start gap-1">
          <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
          <span className="break-words" data-testid={`pkg-address-${stop.final_order}`}>
            {fullAddress(stop) || "—"}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1 text-[10px] font-mono flex-wrap">
          {status === "skipped" && reason && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">
              {reason === "awizo" ? "Awizo" : reason === "return" ? "Zwrot" : "Powtórka"}
            </span>
          )}
          {stop.is_multipack && (
            <span
              data-testid={`multipack-badge-${stop.final_order}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-bold uppercase tracking-wider"
              title={`Multipack: ${stop.multipack_group_size} paczek pod tym adresem — 1× pełna stawka + ${stop.multipack_group_size - 1}× 0,50 zł`}
            >
              Multipack ×{stop.multipack_group_size}
            </span>
          )}
          {stop.is_fixed && (
            <span
              data-testid={`fixed-badge-${stop.final_order}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider"
              title="Stały punkt trasy — dodany automatycznie z szablonu"
            >
              📌 Stały
            </span>
          )}
          {(stop.time_from || stop.time_to) && (
            <span
              data-testid={`time-window-${stop.final_order}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-bold tracking-wider"
              title="Okno godzinowe doręczenia"
            >
              🕒 {stop.time_from || "…"}–{stop.time_to || "…"}
            </span>
          )}
          {stop.parcel_number && (
            <span className="text-zinc-500 truncate max-w-[140px]" title={stop.parcel_number}>
              #{stop.parcel_number}
            </span>
          )}
          {/* Audit stamp — who changed status when, plus optional geolocation.
              Color-coded by driver (visible when 2+ drivers share the plan). */}
          {stop.status_at && (stop.status_by_name || stop.status_by) && (
            <span
              data-testid={`stop-audit-${stop.final_order}`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ring-1 text-[10px] ${driverTone(stop.status_by)}`}
              title={[
                stop.status_by_name && `Doręczył/zmienił: ${stop.status_by_name}`,
                stop.status_at && `Kiedy: ${new Date(stop.status_at).toLocaleString("pl-PL")}`,
                stop.status_location && `GPS: ${stop.status_location.lat.toFixed(5)}, ${stop.status_location.lng.toFixed(5)}${
                  stop.status_location.accuracy ? ` ±${Math.round(stop.status_location.accuracy)}m` : ""
                }`,
              ].filter(Boolean).join("\n")}
            >
              <User2 className="w-2.5 h-2.5" />
              <span className="font-semibold">{stop.status_by_name || "system"}</span>
              <span className="opacity-50"> · </span>
              <span className="font-mono">
                {new Date(stop.status_at).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}
              </span>
              {stop.status_location && (
                <a
                  href={`https://www.google.com/maps?q=${stop.status_location.lat},${stop.status_location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`stop-gps-link-${stop.final_order}`}
                  className="ml-0.5 hover:scale-125 transition-transform"
                  title={`Pokaż lokalizację na mapie (±${Math.round(stop.status_location.accuracy || 0)}m)`}
                >
                  <MapPin className="w-3 h-3" />
                </a>
              )}
            </span>
          )}
          {stop.cod_amount > 0 && (
            <span className="text-emerald-700 font-bold">
              POBRANIE {Number(stop.cod_amount).toLocaleString("pl-PL")} zł
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-1.5 items-end sm:items-center">
        {stop.phone && !selectionMode && (
          <a
            href={`tel:${stripPhone(stop.phone)}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`btn-call-${stop.final_order}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
            title={`Zadzwoń ${stop.phone}`}
          >
            <Phone className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Zadzwoń</span>
          </a>
        )}
        {!selectionMode && (
        <div className="flex gap-1.5">
          <StatusBtn active={status === "done"} onClick={onSetDone} disabled={disabled}
                     testId={`btn-done-${stop.final_order}`} tone="emerald" icon={PackageCheck}>
            Doręczono
          </StatusBtn>
          <StatusBtn active={status === "skipped"} onClick={onAskUndelivered} disabled={disabled}
                     testId={`btn-undelivered-${stop.final_order}`} tone="amber" icon={PackageX}>
            Niedoręczona
          </StatusBtn>
          <StatusBtn active={status === "pending"} onClick={onSetPending} disabled={disabled}
                     testId={`btn-pending-${stop.final_order}`} tone="zinc" icon={Clock}>
            Cofnij
          </StatusBtn>
        </div>
        )}
      </div>
      {saving && <Loader2 className="w-4 h-4 animate-spin text-zinc-400 self-center" />}
    </div>
  );
}
/**
 * Admin GPS audit map — plots every delivered/skipped stop that captured a
 * live device location. Marker color is derived from the driver who stamped
 * the status, so shared multi-driver routes are instantly diagnosable.
 *
 * Falls back gracefully if no stops carry `status_location` yet (early trip)
 * or if the assignment was fully worked without GPS consent.
 */
function AuditMap({ stops }) {
  const points = useMemo(
    () =>
      (stops || []).filter(
        (s) =>
          s.status_location &&
          typeof s.status_location.lat === "number" &&
          typeof s.status_location.lng === "number",
      ),
    [stops],
  );

  // Group by driver for the legend.
  const drivers = useMemo(() => {
    const m = new Map();
    points.forEach((s) => {
      const uid = s.status_by || "__system__";
      if (!m.has(uid)) {
        m.set(uid, { name: s.status_by_name || "system", count: 0 });
      }
      m.get(uid).count += 1;
    });
    return Array.from(m.entries());
  }, [points]);

  if (points.length === 0) {
    return (
      <div
        data-testid="audit-map-empty"
        className="p-6 text-center text-sm text-zinc-500 bg-zinc-50 rounded-b-2xl"
      >
        <MapIcon className="w-5 h-5 mx-auto mb-1 text-zinc-400" />
        Brak zapisanych lokalizacji GPS — kierowcy nie udostępnili pozycji
        podczas zmiany statusu.
      </div>
    );
  }

  // Center on the centroid — good default for Poland-wide or city routes alike.
  const avgLat = points.reduce((a, s) => a + s.status_location.lat, 0) / points.length;
  const avgLng = points.reduce((a, s) => a + s.status_location.lng, 0) / points.length;

  return (
    <div data-testid="audit-map">
      {/* Legend */}
      {drivers.length > 0 && (
        <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-100 flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            // Legenda:
          </span>
          {drivers.map(([uid, info]) => (
            <span
              key={uid}
              data-testid={`audit-map-legend-${uid}`}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white ring-1 ring-zinc-200 text-[11px] font-semibold text-zinc-700"
            >
              <span
                className="w-2.5 h-2.5 rounded-full ring-2 ring-white shadow-sm"
                style={{ background: driverHex(uid === "__system__" ? "" : uid) }}
              />
              {info.name}
              <span className="opacity-50 font-mono">· {info.count}</span>
            </span>
          ))}
        </div>
      )}

      <div className="h-[420px] w-full">
        <MapContainer
          center={[avgLat, avgLng]}
          zoom={12}
          scrollWheelZoom
          className="w-full h-full"
        >
          <TileLayer
            attribution="&copy; OpenStreetMap"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {points.map((s) => {
            const color = driverHex(s.status_by);
            const isDone = s.status === "done";
            return (
              <CircleMarker
                key={s.final_order}
                center={[s.status_location.lat, s.status_location.lng]}
                radius={9}
                pathOptions={{
                  color: "#ffffff",
                  weight: 2,
                  fillColor: color,
                  fillOpacity: isDone ? 0.95 : 0.55,
                  dashArray: isDone ? undefined : "3 3",
                }}
              >
                <Popup>
                  <div className="text-xs space-y-1 min-w-[180px]">
                    <div className="font-bold text-zinc-900">
                      #{s.final_order} · {s.client_name || "Paczka"}
                    </div>
                    <div className="text-zinc-600">{fullAddress(s) || "—"}</div>
                    <div className="flex items-center gap-1 flex-wrap pt-1">
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                        style={{ background: color }}
                      >
                        <User2 className="w-3 h-3" />
                        {s.status_by_name || "system"}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          isDone
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {isDone ? "Doręczono" : "Niedoręczono"}
                      </span>
                    </div>
                    {s.status_at && (
                      <div className="text-[10px] font-mono text-zinc-500">
                        {new Date(s.status_at).toLocaleString("pl-PL")}
                      </div>
                    )}
                    {s.status_location.accuracy && (
                      <div className="text-[10px] font-mono text-zinc-400">
                        ±{Math.round(s.status_location.accuracy)} m
                      </div>
                    )}
                    <a
                      href={`https://www.google.com/maps?q=${s.status_location.lat},${s.status_location.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline pt-0.5"
                    >
                      <MapPin className="w-3 h-3" /> Otwórz w Google Maps
                    </a>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}



function UndeliveredModal({ stop, onCancel, onPick }) {
  return (
    <div onClick={onCancel}
         className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-3"
         data-testid="reason-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl">
        <div className="p-5 border-b border-zinc-100">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            {stop.final_order != null
              ? <>// Paczka #{stop.final_order} niedoręczona</>
              : <>// {stop.client_name || "Wybrane paczki"} — niedoręczone</>}
          </div>
          <h2 className="font-heading font-black uppercase tracking-tight text-xl mt-1">
            Dlaczego niedoręczona?
          </h2>
        </div>
        <div className="p-3 grid gap-2">
          {UNDELIVERED_REASONS.map((r) => (
            <button key={r.id} onClick={() => onPick(r.id)}
                    data-testid={`btn-reason-${r.id}`}
                    className="relative overflow-hidden group rounded-2xl p-4 text-left ring-1 ring-zinc-200 hover:ring-zinc-400 transition-all hover:scale-[1.01]">
              <div className={`absolute inset-0 opacity-15 bg-gradient-to-br ${r.tone}`} />
              <div className="relative flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${r.tone} text-white flex items-center justify-center ring-1 ring-white/25 shadow-md`}>
                  <r.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 font-heading font-black uppercase tracking-tight">
                  {r.label}
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-400" />
              </div>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-zinc-100">
          <button onClick={onCancel}
                  className="w-full py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-sm font-bold uppercase tracking-widest">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBtn({ active, onClick, disabled, testId, tone, icon: Icon, children }) {
  const map = {
    emerald: active ? "bg-emerald-500 text-white shadow-md" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-200",
    amber:   active ? "bg-amber-500 text-white shadow-md"   : "bg-amber-50 text-amber-800 hover:bg-amber-100 ring-1 ring-amber-200",
    zinc:    active ? "bg-zinc-700 text-white shadow-md"    : "bg-zinc-50 text-zinc-600 hover:bg-zinc-100 ring-1 ring-zinc-200",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-90 ${map[tone]}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{children}</span>
    </button>
  );
}

function MiniStat({ label, value, icon: Icon, tone }) {
  const map = {
    white:   "bg-white/15 text-white ring-white/25",
    emerald: "bg-emerald-500/25 text-emerald-100 ring-emerald-300/40",
    amber:   "bg-amber-500/25 text-amber-100 ring-amber-300/40",
    cyan:    "bg-cyan-500/25 text-cyan-100 ring-cyan-300/40",
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${map[tone]}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      <span className="font-heading font-black">{value}</span>
    </div>
  );
}

function Chip({ label, value, tone }) {
  const map = {
    cyan:    "bg-cyan-500/25 text-cyan-100 ring-cyan-300/40",
    emerald: "bg-emerald-500/25 text-emerald-100 ring-emerald-300/40",
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${map[tone]}`}>
      <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      <span className="font-heading font-black text-base">{value}</span>
    </div>
  );
}
